//
// ============================================================================
// IMPORTS
// ============================================================================
//

import type { ErrorType } from "@/composables/utils/useErrorBoundary";
import { LaboratoryClient } from "../../services/api/domains/laboratory/laboratory.client";
import type {
  LogoFormat,
  TermsOfUse,
} from "../../services/api/domains/laboratory/laboratory.types";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Laboratory Identity Composable
 *
 * Manages laboratory identity data (logo, name, terms) for the LIS Portal.
 *
 * Responsibilities:
 * - Fetch laboratory logo in various formats
 * - Fetch laboratory name (localized)
 * - Fetch terms of use (localized)
 * - Cache data in state
 * - Handle errors with user-friendly messages
 *
 * @param key - Unique key for state isolation (default: "laboratory")
 */
export const useLaboratory = (key = "laboratory") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  const config = useRuntimeConfig();
  const { getToken } = useAuth();
  const { handleError } = useErrorBoundary();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  // Separate loading states for each operation to allow parallel fetching
  const loadingLogo = useState<boolean>(`${key}.loading.logo`, () => false);
  const loadingName = useState<boolean>(`${key}.loading.name`, () => false);
  const loadingTerms = useState<boolean>(`${key}.loading.terms`, () => false);

  const laboratoryName = useState<string | null>(`${key}.name`, () => null);
  const laboratoryNameCulture = useState<string | null>(`${key}.nameCulture`, () => null);
  const termsOfUse = useState<TermsOfUse | null>(`${key}.terms`, () => null);
  const error = useState<string | null>(`${key}.error`, () => null);
  const errorType = useState<ErrorType | null>(`${key}.errorType`, () => null);

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Fetch laboratory logo
   *
   * Purpose:
   * Retrieve the laboratory's logo in default format.
   *
   * @param signal - Optional abort signal
   * @returns Logo blob
   */
  const fetchLogo = async (signal?: AbortSignal | null): Promise<Blob | null> => {
    loadingLogo.value = true;
    error.value = null;
    errorType.value = null;
    try {
      const token = await getToken();
      const client = new LaboratoryClient(config.public.API_BASE_URL as string, token);
      return await client.getLogo(signal);
    } catch (err) {
      const result = handleError(err, {
        operation: "fetchLogo",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message;
      errorType.value = result.type;
      return null;
    } finally {
      loadingLogo.value = false;
    }
  };

  /**
   * Fetch laboratory logo by format
   *
   * Purpose:
   * Retrieve the laboratory's logo in specified format (PNG or SVG).
   *
   * @param format - Logo format (png or svg)
   * @param signal - Optional abort signal
   * @returns Logo blob
   */
  const fetchLogoByFormat = async (
    format: LogoFormat,
    signal?: AbortSignal | null,
  ): Promise<Blob | null> => {
    loadingLogo.value = true;
    error.value = null;
    errorType.value = null;
    try {
      const token = await getToken();
      const client = new LaboratoryClient(config.public.API_BASE_URL as string, token);
      return await client.getLogoByFormat(format, signal);
    } catch (err) {
      const result = handleError(err, {
        operation: "fetchLogoByFormat",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message;
      errorType.value = result.type;
      return null;
    } finally {
      loadingLogo.value = false;
    }
  };

  /**
   * Fetch laboratory name (with cache)
   *
   * Purpose:
   * Retrieve the laboratory's name, optionally localized.
   * Returns cached value if available and culture matches.
   *
   * @param culture - Optional language code
   * @param signal - Optional abort signal
   * @param forceRefresh - Force refresh even if cached (default: false)
   * @returns Laboratory name
   */
  const fetchName = async (
    culture?: string,
    signal?: AbortSignal | null,
    forceRefresh = false,
  ): Promise<string | null> => {
    // Return cached value if available and culture matches (unless forcing refresh)
    if (!forceRefresh && laboratoryName.value && laboratoryNameCulture.value === culture) {
      return laboratoryName.value;
    }

    // If already loading, wait a bit and return cached value to prevent duplicate requests
    if (loadingName.value) {
      return laboratoryName.value;
    }

    loadingName.value = true;
    error.value = null;
    errorType.value = null;
    try {
      const token = await getToken();
      const client = new LaboratoryClient(config.public.API_BASE_URL as string, token);
      const name = await client.getName(culture, signal);
      laboratoryName.value = name;
      laboratoryNameCulture.value = culture || null;
      return name;
    } catch (err) {
      const result = handleError(err, {
        operation: "fetchName",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message;
      errorType.value = result.type;
      return null;
    } finally {
      loadingName.value = false;
    }
  };

  /**
   * Fetch terms of use (with cache)
   *
   * Purpose:
   * Retrieve the laboratory's terms of use, localized.
   * Returns cached value if available and culture matches.
   *
   * @param culture - Optional language code
   * @param signal - Optional abort signal
   * @param forceRefresh - Force refresh even if cached (default: false)
   * @returns Terms of use
   */
  const fetchTermsOfUse = async (
    culture?: string,
    signal?: AbortSignal | null,
    forceRefresh = false,
  ): Promise<TermsOfUse | null> => {
    // Return cached value if available and culture matches (unless forcing refresh)
    if (!forceRefresh && termsOfUse.value && (termsOfUse.value as any).culture === culture) {
      return termsOfUse.value;
    }

    loadingTerms.value = true;
    error.value = null;
    errorType.value = null;
    try {
      const token = await getToken();
      const client = new LaboratoryClient(config.public.API_BASE_URL as string, token);
      const terms = await client.getTermsOfUse(culture, signal);
      termsOfUse.value = terms;
      return terms;
    } catch (err) {
      const result = handleError(err, {
        operation: "fetchTermsOfUse",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message;
      errorType.value = result.type;
      return null;
    } finally {
      loadingTerms.value = false;
    }
  };

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  /**
   * Check if name is cached for current culture
   */
  const hasCachedName = computed(() =>
    Boolean(laboratoryName.value && laboratoryNameCulture.value),
  );

  /**
   * Check if terms are cached
   */
  const hasCachedTerms = computed(() => Boolean(termsOfUse.value));

  /**
   * Check if name is cached for specific culture
   */
  const hasCachedNameFor = (culture?: string): boolean => {
    return Boolean(laboratoryName.value && laboratoryNameCulture.value === culture);
  };

  //
  // ============================================================================
  // RETURN PUBLIC API
  // ============================================================================
  //

  return {
    // State (readonly for external consumers)
    loadingLogo: readonly(loadingLogo),
    loadingName: readonly(loadingName),
    loadingTerms: readonly(loadingTerms),
    laboratoryName: readonly(laboratoryName),
    termsOfUse: readonly(termsOfUse),
    error: readonly(error),
    errorType: readonly(errorType),

    // Computed caching helpers
    hasCachedTerms,

    // Methods
    fetchLogo,
    fetchLogoByFormat,
    fetchName,
    fetchTermsOfUse,
    hasCachedNameFor,
  };
};
